import { Location } from '@angular/common';
import { inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { routerNavigatedAction } from '@ngrx/router-store';
import { ActionType, Store, createSelector } from '@ngrx/store';
import { ConfirmationService } from 'primeng/api';
import { generateWord } from 'quill-to-word';
import { EMPTY, catchError, distinctUntilChanged, exhaustMap, filter, from, map, merge, of, switchMap, tap } from 'rxjs';

import { overviewTimelineFeature } from 'src/app/job/job-overview/overview-timeline/overview-timeline.feature';
import { JobToolActions } from 'src/app/job/job-tool.actions';
import { NEW_OPPORTUNITY_STEPS, NewOpportunityStep } from 'src/app/new-opportunity/new-opportunity.state';
import { UnsavedChangesBannerActions } from 'src/app/shared/unsaved-changes-banner/unsaved-changes-banner.actions';
import { selectSaveableJobPageChanges } from 'src/app/shared/unsaved-changes-banner/unsaved-changes-banner.selectors';
import { ActionSource } from 'src/app/state/action-sources';
import { UnsavedChangesActions } from 'src/app/state/unsaved-changes.actions';
import { unsavedChangesFeature } from 'src/app/state/unsaved-changes.reducer';

import { LoadingState } from 'src/app/utilities/state.util';

import {
    CreateNewJobGQL,
    CreateNewJobMutationVariables,
    CreateUserInputWithRole,
    SetFieldValueInput,
} from '../../generated/graphql.generated';
import { PlusAuthenticationService } from '../core/auth/plus-auth.service';
import { dayjs } from '../core/dayjs/dayjs';
import { FIELD_CONFIG, JOB_LOCATION_TYPES_V3, JOB_ROLE_MAP, JOB_SUMMARIES, LocationType, eventTypeInfoMapV2 } from '../global.constants';
import { isDeltaBlank } from '../job/job-activity/comments.utils';
import { jobCreateCustomerFeature } from '../job/job-create/job-create-customer/job-create-customer.feature';
import { selectCustomerRole } from '../job/job-create/job-create-state/job-create.selectors';
import { jobCreateSummaryFeature } from '../job/job-create/job-create-summary/job-create-summary.feature';
import { SummaryType, summaryKeys } from '../job/job-create/job-create-summary/job-create-summary.reducer';
import { getCustomerProfileEdit, mapAdditionalContactsToInput, parseFullName } from '../job/job-helpers';
import { jobInventoryFeature } from '../job/job-inventory/job-inventory.feature';
import { JobInventory } from '../job/job-inventory/job-inventory.state';
import { LocationDetailsEditorKey, billingAddressChanged, encodeMetadataAccess, getLocationDetailsMetadataKey, resolveBillingAddressInput, resolveBillingLocationEditor } from '../job/job-locations-helpers';
import { JobLocationsOverviewActions } from '../job/job-overview/overview-locations/overview-locations.actions';
import { overviewLocationsFeature } from '../job/job-overview/overview-locations/overview-locations.feature';
import {
  CALCULATOR_INPUT_METADATA_PREFIX,
  CALCULATOR_OUTPUT_METADATA_PREFIX,
  TRANSIENT_CALCULATOR_KEYS,
  selectCalculatorUrl,
  selectCustomerName,
  selectJob,
  selectJobId,
} from '../job/job-tool.reducer';
import { quickjsFeature } from '../quickjs/quickjs.feature';
import { BrandingService } from '../services/branding.service';
import { brandingFeature } from '../state/branding.store';
import { selectUrl } from '../state/router';

import { NewOpportunityActions } from './new-opportunity.actions';
import { newOpportunityFeature } from './new-opportunity.feature';
import { selectLocationStepControlStatus, selectWillGenerateEstimatePreview } from './step-navigation/step-navigation.component';

export const syncStepToUrl = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    router = inject(Router),
    route = inject(ActivatedRoute),
    location = inject(Location),
) => {
    return actions$.pipe(
      ofType(routerNavigatedAction.type),
      map(({ payload: { event: { urlAfterRedirects } } }) => new URL(urlAfterRedirects, window.location.origin).pathname),
      // New-opportunity flow lives at the create (/jobs/new) and edit (/jobs/:jobId/edit) routes.
      filter((url) => url.startsWith('/jobs/new') || /^\/jobs\/[^/]+\/edit/.test(url)),
      switchMap(() => store.select(newOpportunityFeature.selectStep).pipe(
        filter(Boolean),
        distinctUntilChanged(),
        tap((step) => location.replaceState(router.createUrlTree([], {
          relativeTo: route,
          queryParams: { step },
          queryParamsHandling: 'merge',
        }).toString())),
      )),
    );
}, { functional: true, dispatch: false });

export const triggerReplayChanges = createEffect((
  actions$ = inject(Actions),
	store = inject(Store),
  router = inject(Router),
) => {
    return actions$.pipe(
      ofType(routerNavigatedAction),
      filter(({ payload: { event: { urlAfterRedirects } } }) => urlAfterRedirects.startsWith('/jobs/new')),
      // Do not replay changes if user preselected
      filter(() => !(router.getCurrentNavigation()?.extras?.state?.existingCustomer)),
      concatLatestFrom(() => store.select(unsavedChangesFeature.selectQueue)),
      map(([ _action, changes ]) => changes.filter((c) => c.source === ActionSource.NEW_OPPORTUNITY_V4)),
      map((changes) => UnsavedChangesActions.replayTriggered({ changes })),
    );
	}, { functional: true, dispatch: true });

export const validateStepChange = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  confirmationService = inject(ConfirmationService),
) => {
    return actions$.pipe(
      ofType(
        NewOpportunityActions.stepClicked,
        NewOpportunityActions.nextStepButtonClicked,
        NewOpportunityActions.previousStepButtonClicked,
      ),
      map((action): NewOpportunityStep | null => {

          if (action.type === NewOpportunityActions.stepClicked.type) {
            return action.nextStep;
          }

          const currentIndex = NEW_OPPORTUNITY_STEPS.indexOf(action.currentStep);

          if (currentIndex === -1) {
            return null;
          }

          const offset = action.type === NewOpportunityActions.nextStepButtonClicked.type ? 1 : -1;

          return NEW_OPPORTUNITY_STEPS[currentIndex + offset] ?? null;
        }
      ),
      filter((step): step is NewOpportunityStep => step !== null),
      concatLatestFrom((nextStep) => {

        const selectRequiresConfirmation = createSelector(
          selectUrl,
          newOpportunityFeature.selectCreateJobWithMissingLocationDataConfirmed,
          overviewLocationsFeature.selectLocationEditors,
          (url, alreadyConfirmed, editors): null | { start: boolean; end: boolean; } => {

            if (!url.startsWith('/jobs/new')) {
              return null;
            }

            if (alreadyConfirmed) {
              return null;
            }

            if (nextStep !== 'inventory') {
              return null;
            }

            if (editors.start?.selectedLocation && editors.end?.selectedLocation) {
              return null;
            }

            return {
              start: Boolean(editors.start?.selectedLocation),
              end: Boolean(editors.end?.selectedLocation),
            }
          },
        );

        return store.select(selectRequiresConfirmation);
      }),
      tap(([ _nextStep, requiresConfirmation ]) => {
        if (requiresConfirmation !== null) {

          const locationsMissing = Object.entries(requiresConfirmation)
            .filter(([ _location, hasLocation ]) => !hasLocation)
            .map(([ location ]) => location)
            .join(', ');

          confirmationService.confirm({
            header: 'Missing Location Data',
            message: `This lead is missing the following locations: ${locationsMissing}. Are you sure you want to continue?`,
            acceptLabel: 'Continue',
            rejectLabel: 'Cancel',
            accept: () => store.dispatch(NewOpportunityActions.createJobWithMissingLocationDataConfirmed()),
          });
        }
      }),
      filter(([ _nextStep, requiresConfirmation ]) => !requiresConfirmation),
      map(([ step ]) => NewOpportunityActions.validateStepChangeSuccess({ step })),
    );
}, { functional: true, dispatch: true });

export const buildCreateJobVariables = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    branding = inject(BrandingService),
) => {
    return actions$.pipe(
      ofType(
        NewOpportunityActions.validateStepChangeSuccess,
        NewOpportunityActions.createJobWithMissingLocationDataConfirmed,
        NewOpportunityActions.locationsCompleted,
      ),
      concatLatestFrom(() => [
        store.select(selectUrl),
        store.select(newOpportunityFeature.selectCreateJobCall),
      ]),
      filter(([
        action,
        url,
        createJobCall,
      ]) => {

        // Only create a job when navigating from the /jobs/new route
        if (!url.startsWith('/jobs/new')) {
          return false;
        }

        if (createJobCall === LoadingState.MUTATING || createJobCall === LoadingState.MUTATED) {
          return false;
        }

        if (action.type === NewOpportunityActions.createJobWithMissingLocationDataConfirmed.type) {
          return true;
        }

        if (action.type === NewOpportunityActions.locationsCompleted.type) {
          return true;
        }

        return action.step === 'inventory';
      }),
      exhaustMap(([ promptedBy ]) => of(null).pipe(
        concatLatestFrom(() => [
          store.select(jobCreateCustomerFeature.selectCustomerEditor),
          store.select(overviewTimelineFeature.selectTimeline),
          store.select(overviewLocationsFeature.selectLocationEditors),
          store.select(overviewLocationsFeature.selectUseEndLocationAsBillingLocation),
          store.select(jobInventoryFeature.selectInventoryEditor),
          store.select(jobCreateSummaryFeature.selectSummaryEditors),
          store.select(brandingFeature.selectCurrency),
          store.select(overviewLocationsFeature.selectServiceArea),
          store.select(selectCustomerRole),
          store.select(jobInventoryFeature.selectYemboOptions),
          store.select(quickjsFeature.selectUserInputs),
          store.select(quickjsFeature.selectCalculatedState),
          store.select(selectCalculatorUrl),
        ]),
        map(([
          _null,
          {
            selectedCustomer,
            fullName,
            phone,
            email,
            additionalContacts,
            company,
            jobOrigin,
            customerType,
            referralSource,
          },
          timeline,
          locationEditors,
          useEndLocationAsBillingLocation,
          inventoryEditor,
          summaryEditors,
          currency,
          serviceArea,
          customerRoleId,
          yemboOptions,
          calculatorInputs,
          calculatorOutputs,
          calculatorUrl,
        ]): {
          vars: CreateNewJobMutationVariables;
        } => {

          const vars: CreateNewJobMutationVariables = {
            stage: 'lead',
            zoneId: serviceArea?.id || branding.currentZone().value.id,
            currency,
            metadata: {
              jobType: customerType,
            },
            locations: [],
            users: [],
            events: [],
            newCustomers: [],
            fields: {
              objectLabel: 'Job',
              fields: [],
            },
          };

          if (jobOrigin) {
            vars.metadata.jobOrigin = jobOrigin;
          }

          // Narrow
          if (!Array.isArray(vars.users)) { return; }
          if (!Array.isArray(vars.newCustomers)) { return; }

          const billingAddress = resolveBillingAddressInput(locationEditors, useEndLocationAsBillingLocation);

          if (selectedCustomer) {
            vars.users.push({
              role: JOB_ROLE_MAP.customerRole,
              userId: selectedCustomer.id,
            });

            // Requirement: creating a lead never overwrites a billing address the customer
            // already has. The checkbox mirrors the end location into the billing slot without
            // the user typing it, so a plain diff would rewrite an existing customer's address
            // to this job's destination on every lead. It is sent only when they have none on
            // file, or when they unchecked the box and entered one deliberately.
            const billingEdited = !useEndLocationAsBillingLocation
              && resolveBillingLocationEditor(locationEditors, useEndLocationAsBillingLocation)?.locationModified;

            // Unlike the job page, the checkbox here is not derived from the selected customer --
            // `existingCustomerSelected` leaves it at its default -- so a difference from what is
            // stored is not on its own evidence that the user meant to change it.
            const sendBillingAddress = (!selectedCustomer.billingAddress || billingEdited)
              && billingAddressChanged(billingAddress, selectedCustomer.billingAddress);

            vars.setCustomerProfile = getCustomerProfileEdit(
              selectedCustomer,
              { fullName, phone, email, company, additionalContacts },
              sendBillingAddress ? billingAddress : undefined,
            );
          } else {

            const { givenName, familyName } = parseFullName(fullName);

            const createUserInput: CreateUserInputWithRole = {
              company,
              familyName,
              givenName,
              role: customerRoleId,
            };

            if (email) {
              createUserInput.email = email;
            }

            if (phone) {
              createUserInput.phone = phone;
            }

            if (additionalContacts.length) {
              createUserInput.additionalContacts = mapAdditionalContactsToInput(additionalContacts);
            }

            if (billingAddress) {
              createUserInput.billingAddress = billingAddress;
            }

            vars.newCustomers.push(createUserInput);
          }

          if (referralSource) {
            vars.fields.fields.push({
              fieldName: FIELD_CONFIG.customer.howHeard.name,
              value: referralSource,
            });
          }

          // Narrow
          if (!Array.isArray(vars.locations)) {
            return;
          }

          if (timeline) {

            const [ startDate, endDate ] = timeline;

            const start = startDate ? dayjs(startDate) : undefined;

            if (start) {
              vars.timeline = start.format('YYYY-MM-DD');
            }

            if (start && endDate) {
              const end = dayjs(endDate);
              vars.timelineDays = end.diff(start, 'days');
            } else if (start) {
              vars.timelineDays = 0;
            }
          }

          const locationTypes: LocationType[] = [ ...JOB_LOCATION_TYPES_V3 ];

          for (const locationType of locationTypes) {

            const editor = locationEditors[locationType];

            if (!editor) { continue; }

            const {
              selectedLocation,
              unitNumber,
            } = editor;

            if (selectedLocation) {
              vars.locations.push({
                locationType,
                newLocationInput: {
                  addressLineOne: selectedLocation.addressLineOne,
                  addressLineTwo: unitNumber,
                  areaCode: selectedLocation.areaCode,
                  city: selectedLocation.city,
                  coordinates: {
                    latitude: selectedLocation.coordinates.latitude,
                    longitude: selectedLocation.coordinates.longitude,
                  },
                  country: selectedLocation.country,
                  countryJurisdiction: selectedLocation.countryJurisdiction,
                  locationType,
                  name: selectedLocation.name,
                  public: false,
                }
              });
            }

            if (locationType !== 'start' && locationType !== 'end') { continue; }

            vars.metadata = vars.metadata || {};

            const locationDetails: Array<{ key: LocationDetailsEditorKey; value: string | undefined }> = [
              { key: 'buildingType', value: editor.buildingType },
              { key: 'size', value: editor.size },
              { key: 'access', value: encodeMetadataAccess(editor) },
            ];

            for (const detail of locationDetails) {
              if (detail.value === undefined || detail.value === null || detail.value === '') { continue; }

              vars.metadata[getLocationDetailsMetadataKey(locationType, detail.key)] = detail.value;
            }
          }

          if (inventoryEditor.rooms.length > 0) {
            const inventory: JobInventory = {
              rooms: inventoryEditor.rooms,
              specialtyItems: inventoryEditor.specialtyItems,
              weightUnit: inventoryEditor.weightUnit,
              volumeUnit: inventoryEditor.volumeUnit,
            };

            const inventoryField: SetFieldValueInput = {
              fieldName: FIELD_CONFIG.jobs.inventory.name,
              value: JSON.stringify(inventory),
            };

            vars.fields.fields.push(inventoryField);
          }

          if (yemboOptions.includes('selfSurvey')) {
            vars.sendSelfSurvey = true;
          }

          // Narrow
          if (!Array.isArray(vars.events)) { return; }

          if (yemboOptions.includes('smartConsult')) {
            vars.events.push({
              sequentialOrder: 0.5,
              status: 'required',
              type: eventTypeInfoMapV2.virtualEstimate.value,
              title: eventTypeInfoMapV2.virtualEstimate.name,
            });
          }

          // Persist any calculator state entered before the job was created,
          // mirroring buildSaveChangesVariables. No need to gate on unsaved
          // changes here: with no job yet, any inputs present were entered by
          // the user this session.
          for (const [ key, value ] of Object.entries(calculatorInputs)) {
            if (TRANSIENT_CALCULATOR_KEYS.has(key)) { continue; }
            vars.metadata[CALCULATOR_INPUT_METADATA_PREFIX + key] = value;
          }

          for (const [ key, value ] of Object.entries(calculatorOutputs)) {

            const allowed = [ 'string', 'number', 'boolean' ];

            if (!(allowed.includes(typeof value))) { continue; }

            vars.metadata[CALCULATOR_OUTPUT_METADATA_PREFIX + key] = value;
          }

          if (calculatorUrl) {
            vars.metadata.calculatorUrl = calculatorUrl;
          }

          for (const key of summaryKeys) {
            vars[key] = { contents: JSON.stringify(summaryEditors[key]) };
          }

          if (vars.fields.fields.length === 0) {
            delete vars.fields;
          }

          return { vars };
        }),
        map(({ vars }) => NewOpportunityActions.buildCreateJobVariablesSuccess({ vars, promptedBy })),
        catchError((error) => of(NewOpportunityActions.buildCreateJobVariablesError({ error }))),
      )),
    );
}, { functional: true, dispatch: true });

export const createJob = createEffect((
    actions$ = inject(Actions),
    createNewJobGQL = inject(CreateNewJobGQL),
) => {
    return actions$.pipe(
      ofType(NewOpportunityActions.buildCreateJobVariablesSuccess),
      exhaustMap(({ vars, promptedBy }) => createNewJobGQL.mutate(vars).pipe(
        map((res) => {
          const { zone } = res.data.createJob;
          const zoneId = zone.type === 'area' ? zone.parents[0].id : zone.id;
          return NewOpportunityActions.createJobSuccess({
            jobId: res.data.createJob.id,
            zoneId,
            promptedBy,
          });
        }),
        catchError((error) => of(NewOpportunityActions.createJobError({ error }))),
      )),
    );
}, { functional: true, dispatch: true });

export const redirectAfterCreateJobSuccess = createEffect((
    actions$ = inject(Actions),
    router = inject(Router),
    plusAuthService = inject(PlusAuthenticationService),
    store = inject(Store),
) => {
    return actions$.pipe(
      ofType(NewOpportunityActions.createJobSuccess),
      switchMap(({ jobId, zoneId }) => from(plusAuthService.setContext(zoneId)).pipe(
        concatLatestFrom(() => store.select(newOpportunityFeature.selectStep)),
        tap(([ _res, step ]) => router.navigate(
          [ `/jobs/${jobId}/edit` ],
          { queryParams: { zone: zoneId, step } },
        )),
      )),
    );
}, { functional: true, dispatch: false });

export const watchChangeStepAndSave = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
) => {

    const changeStepButtonClickedActions = [
      NewOpportunityActions.nextStepButtonClicked,
      NewOpportunityActions.previousStepButtonClicked,
      NewOpportunityActions.stepClicked,
    ];

    const changeStepButtonClickedActionsByType = new Set(changeStepButtonClickedActions.map((a) => a.type as string));

    const changeStepButtonClickedNoChanges$ = actions$.pipe(
      ofType(...changeStepButtonClickedActions),
      concatLatestFrom(() => store.select(selectSaveableJobPageChanges)),
      filter(([ _action, changes ]) => changes.length === 0),
      map(([ action ]) => action),
    );

    const changeStepButtonClickedChangesSaved$ = actions$.pipe(
      ofType(JobToolActions.saveChangesSuccess),
      filter(({ promptedBy }) => changeStepButtonClickedActionsByType.has(promptedBy.type)),
      map(({ promptedBy }) => promptedBy as ActionType<typeof changeStepButtonClickedActions[number]>),
    );

    return merge(changeStepButtonClickedNoChanges$, changeStepButtonClickedChangesSaved$).pipe(
      map((promptedBy) => NewOpportunityActions.changeStepAndSaveChangesSuccess({ promptedBy })),
    );
}, { functional: true, dispatch: true });

export const goToJobPage = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    router = inject(Router),
) => {

    const clickedNextOnLastStep$ = actions$.pipe(
      ofType(NewOpportunityActions.changeStepAndSaveChangesSuccess),
      filter(({ promptedBy }) => {

        if (promptedBy.type !== NewOpportunityActions.nextStepButtonClicked.type) return false;

        const { currentStep } = (promptedBy as ActionType<typeof NewOpportunityActions.nextStepButtonClicked>);

        return currentStep === NEW_OPPORTUNITY_STEPS[NEW_OPPORTUNITY_STEPS.length - 1];
      }),
    );

    const clickedGoToJobNoChanges$ = actions$.pipe(
      ofType(NewOpportunityActions.goToJobButtonClicked),
      concatLatestFrom(() => store.select(selectSaveableJobPageChanges)),
      filter(([ _action, changes ]) => changes.length === 0),
      map(([ action ]) => action),
    );

    const clickedGoToJobChangesSaved$ = actions$.pipe(
      ofType(JobToolActions.saveChangesSuccess),
      filter(({ promptedBy }) => promptedBy.type === NewOpportunityActions.goToJobButtonClicked.type),
    );

    const clickedGoToJobAndDiscardedChanges$ = actions$.pipe(
      ofType(NewOpportunityActions.discardChangesBeforeNavigatingToJobPageConfirmed)
    );

    return merge(
      clickedNextOnLastStep$,
      clickedGoToJobNoChanges$,
      clickedGoToJobChangesSaved$,
      clickedGoToJobAndDiscardedChanges$,
    ).pipe(
      concatLatestFrom((action) => [
        store.select(selectJobId),
        store.select(createSelector(
          selectWillGenerateEstimatePreview,
          (generateEstimatePreview) => action.type ===NewOpportunityActions.changeStepAndSaveChangesSuccess.type && generateEstimatePreview,
        )),
      ]),
      // No jobId means the job is still being created; navigating now would land on /jobs/undefined
      filter(([ _action, jobId ]) => Boolean(jobId)),
      tap(([
        _action,
        jobId,
        generateEstimatePreview,
      ]) => router.navigate([ '/jobs', jobId, generateEstimatePreview ? 'workorders' : 'overview' ])),
      map(([ _action, _jobId, generateEstimatePreview ]) => NewOpportunityActions.navigateToJobPageSuccess({ generateEstimatePreview })),
    );
}, { functional: true, dispatch: true });

export const clearNewOpportunityChanges = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(
        UnsavedChangesBannerActions.discardUnsavedChangesButtonClicked,
      ),
      concatLatestFrom(() => store.select(selectUrl)),
      filter(([ _action, url ]) => url.startsWith('/jobs/new')),
      map(() => NewOpportunityActions.unsavedChangesCleared()),
    );
}, { functional: true, dispatch: true });

export const downloadActiveSummary = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(NewOpportunityActions.downloadSummaryButtonClicked),
      concatLatestFrom(() => [
        store.select(jobCreateSummaryFeature.selectSummaryEditors),
        store.select(selectJob),
        store.select(selectCustomerName),
      ]),
      map(([ _action, summaryEditors, job, customerName ]) => {
        const { summaryIndex, crewSummary, adminSummary, customerSummary } = summaryEditors;
        const summaryValues: Record<SummaryType, typeof crewSummary> = { crewSummary, adminSummary, customerSummary };
        const summaryKey = JOB_SUMMARIES[summaryIndex]?.key;
        const summaryDelta = summaryKey ? summaryValues[summaryKey] : undefined;
        const summaryName = JOB_SUMMARIES[summaryIndex]?.name ?? 'Summary';
        const jobCode = job?.code ?? 'Job';
        const dateTime = dayjs().format('YYYY/MM/DD HH:mm');
        const fileName = `${jobCode} ${customerName} ${summaryName} ${dateTime}`
          .replace(/[\\/:*?"<>|]+/g, '-')
          .replace(/\s+/g, ' ')
          .trim() + '.docx';

        return {
          fileName,
          summaryDelta,
        };
      }),
      filter(({ summaryDelta }) => summaryDelta !== undefined && !isDeltaBlank(summaryDelta)),
      exhaustMap(({ fileName, summaryDelta }) => from(generateWord(summaryDelta!, { exportAs: 'blob' })).pipe(
        tap((docxBlob) => {
          const url = URL.createObjectURL(docxBlob as Blob);
          const downloadLink = document.createElement('a');

          downloadLink.href = url;
          downloadLink.download = fileName;
          downloadLink.click();

          URL.revokeObjectURL(url);
        }),
        switchMap(() => EMPTY),
        catchError((error) => {
          return of(NewOpportunityActions.downloadSummaryError({
            error: error instanceof Error ? error : new Error('Failed to generate summary .docx'),
          }));
        }),
      )),
    );
}, { functional: true, dispatch: true });

export const initializeEndLocationAfterStartWizard = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(JobLocationsOverviewActions.wizardDetailsLoaded),
      filter(({ completed, locationType }) => completed && locationType === 'start'),
      concatLatestFrom(() => [
        store.select(selectUrl),
      ]),
      filter(([ _action, url ]) => url.startsWith('/jobs/new')),
      map(() => NewOpportunityActions.startLocationWizardCompleted()),
    );
}, { functional: true, dispatch: true });

export const completeLocationsAfterEndWizard = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(JobLocationsOverviewActions.wizardDetailsLoaded),
      filter(({ completed, locationType }) => completed && locationType === 'end'),
      concatLatestFrom(() => [
        store.select(selectUrl),
        store.select(overviewLocationsFeature.selectLocationEditors),
        store.select(selectLocationStepControlStatus),
      ]),
      filter(([ _action, url, locationEditors, locationStepControlStatus ]) => {

        if (!url.startsWith('/jobs/new')) {
          return false;
        }

        if (!locationEditors.start?.selectedLocation || !locationEditors.end?.selectedLocation) {
          return false;
        }

        return Object.values(locationStepControlStatus).every((status) => status.error === null);
      }),
      map(() => NewOpportunityActions.locationsCompleted()),
    );
}, { functional: true, dispatch: true });

export const setDefaultSummary = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(NewOpportunityActions.changeStepAndSaveChangesSuccess),
      concatLatestFrom(() => store.select(newOpportunityFeature.selectStep)),
      map(([ _action, step ]): typeof JOB_SUMMARIES[number]['key'] => {
        switch (step) {
          case 'customer':
          case 'locations':
            return 'adminSummary';
          case 'inventory':
            return 'customerSummary';
          case 'workorders':
            return 'crewSummary';
          default:
            throw new Error(`Unhandled step ${step}`);
        }
      }),
      map((summaryKey) => JOB_SUMMARIES.findIndex((s) => s.key === summaryKey)),
      map((summaryIndex) => NewOpportunityActions.defaultSummaryIndexLoaded({ summaryIndex })),
    );
}, { functional: true, dispatch: true });

export const confirmDiscardInvalidChangesBeforeNavigatingToJobPage = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  confirmationService = inject(ConfirmationService),
) => {
    return actions$.pipe(
      ofType(NewOpportunityActions.goToJobButtonClicked),
      concatLatestFrom(() => store.select(newOpportunityFeature.selectControlMeta)),
      filter(([ _action, controlMeta ]) => Object.values(controlMeta).some((f) => f.errors !== null)),
      tap(() => confirmationService.confirm({
        header: 'Discard Invalid Data?',
        message: 'This form contains invalid data. Do you want to discard your changes before proceeding?',
        acceptLabel: 'Discard',
        accept: () => store.dispatch(NewOpportunityActions.discardChangesBeforeNavigatingToJobPageConfirmed()),
      })),
    );
}, { functional: true, dispatch: false });
